import { formatActiveNumber } from "./formatting";
import { catalogCoverage } from "./catalogCoverage";
import { BUILT_IN_SOURCE_LOCALE, type InstallableLocale } from "./preparedLocales";

export type MessageValues = Readonly<Record<string, string | number>>;

// Only quantity placeholders carry numeric presentation semantics. A generic
// numeric value may instead be a version, port, identifier or other technical value.
const QUANTITY_PARAMETERS = new Set([
  "count", "total", "page", "pages", "score", "confidence", "used", "limit",
  "remaining", "active", "staged", "days", "hours", "minutes", "seconds",
  "size", "completed", "pending", "available", "maximum", "minimum",
]);

export function formatMessage(template: string, values?: MessageValues): string {
  if (!values) return template;
  return template.replace(/\{([a-zA-Z0-9_]+)\}/g, (match, key: string) => {
    const value = values[key];
    if (value === undefined) return match;
    return typeof value === "number" && QUANTITY_PARAMETERS.has(key)
      ? formatActiveNumber(value)
      : String(value);
  });
}

/** Feature catalogs share the platform locale inventory and source fallback. */
export function createFeatureCatalog<const Source extends Readonly<Record<string, string>>>(
  source: Source,
  translations: Partial<Record<InstallableLocale, Readonly<Partial<Record<keyof Source, string>>>>>,
) {
  const messages = Object.freeze({ ...source });
  const localized = Object.fromEntries(Object.entries(translations).map(([locale, catalog]) =>
    [locale, Object.freeze({ ...catalog })],
  )) as Partial<Record<InstallableLocale, Readonly<Partial<Record<keyof Source, string>>>>>;
  return {
    sourceLocale: BUILT_IN_SOURCE_LOCALE,
    message(locale: InstallableLocale, key: keyof Source): string {
      const translated = locale !== BUILT_IN_SOURCE_LOCALE && Object.hasOwn(localized, locale)
        ? localized[locale]?.[key]
        : undefined;
      return typeof translated === "string" && translated.trim() ? translated : messages[key];
    },
    coverage(locale: InstallableLocale) {
      return catalogCoverage(messages, locale === BUILT_IN_SOURCE_LOCALE ? messages : localized[locale] ?? {});
    },
  };
}
